import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { RetentionCategory } from '@/lib/retention';

export type TemplateCategory = RetentionCategory | 'geral';

export interface MessageTemplate {
  id: string;
  personal_id: string | null;
  category: TemplateCategory;
  name: string;
  body: string;
  is_default: boolean;
}

/**
 * Lista os templates visíveis pro PT logado: os próprios + os globais
 * (personal_id IS NULL) — a RLS já cuida desse filtro automaticamente.
 */
export function useMessageTemplates(personalId: string) {
  const queryClient = useQueryClient();

  const { data: templates = [], isLoading: loading } = useQuery({
    queryKey: ['message-templates', personalId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('message_templates')
        .select('*')
        .order('category')
        .order('name');
      if (error) throw error;
      return data as MessageTemplate[];
    },
    enabled: !!personalId,
    staleTime: 5 * 60 * 1000,
  });

  const { mutateAsync: createTemplate } = useMutation({
    mutationFn: async (input: { category: TemplateCategory; name: string; body: string }) => {
      const { data, error } = await supabase
        .from('message_templates')
        .insert({ ...input, personal_id: personalId, is_default: false })
        .select()
        .single();
      if (error) throw error;
      return data as MessageTemplate;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['message-templates', personalId] }),
  });

  const { mutateAsync: updateTemplate } = useMutation({
    mutationFn: async ({ id, ...updates }: { id: string; name?: string; body?: string; category?: TemplateCategory }) => {
      const { data, error } = await supabase
        .from('message_templates')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data as MessageTemplate;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['message-templates', personalId] }),
  });

  const { mutateAsync: deleteTemplate } = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('message_templates').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['message-templates', personalId] }),
  });

  return { templates, loading, createTemplate, updateTemplate, deleteTemplate };
}
